import { useRef } from "react";
import { Link } from "react-router-dom";
import { LIBRARY_PRICES, offerHref } from "@/lib/offers";
import { SPRINT } from "@/lib/pricing";
import { trackOfferClick, useOfferView } from "@/lib/offerTracking";
import { NewsletterSignupCard } from "@/components/NewsletterSignupCard";

/**
 * Which paid-products column the footer carries.
 *   jobs     (default) job-board pages: the free Remote Accountant signup
 *            (NewsletterSignupCard, surface "footer"). Until 2026-11-03 this was a
 *            paid "For your search" column led by the Still Remote pass: about
 *            10,400 views from Oct 20 and no clicks.
 *   library  /library, the Library landing pages and the /learn pages
 *            (LibraryLayout, Cpe, Conferences): the Library and close tools.
 *            One offer_view keyed library, surface "footer", as before.
 * Job-board visitors are job seekers: the Library footer column drew 4,824
 * views and 0 clicks in the 7 days to 2026-10-27, so it stays on the pages
 * whose readers run a close.
 */
export type FooterAudience = "jobs" | "library";

/** The Library and close tools column. One offer_view for the column, offer_click per link, surface "footer". */
const LibraryColumn = () => {
  const ref = useRef<HTMLDivElement>(null);
  useOfferView(ref, "library", "footer");
  const link = (key: string, to: string, label: string) => (
    <li><Link to={to} onClick={() => trackOfferClick(key, "footer")}>{label}</Link></li>
  );
  return (
    <div ref={ref} className="footer__col">
      <h5>Library and tools</h5>
      <ul>
        {link("library", "/library", `AuditFriendly Library, from ${LIBRARY_PRICES.itemFrom}`)}
        {link("close_prompt_playbook", "/playbook", `Close Prompt Playbook, ${LIBRARY_PRICES.close_prompt_playbook}`)}
        {link("pro_month_end_close_kit", "/library/pros", "For accounting pros")}
        {link("smb_year_end_kit", "/small-business", "For small business owners")}
        {link("search_sprint", "/sprint", `Search Sprint, ${SPRINT.priceDisplay} once`)}
      </ul>
    </div>
  );
};

export const HomeFooter = ({ audience = "jobs" }: { audience?: FooterAudience } = {}) => (
  <footer className="footer">
    <div className="container">
      <div className="footer__top">
        <div className="footer__col footer__brand">
          <div className="nav__logo" style={{ fontSize: 20 }}>
            <img src="/af-wordmark.svg" alt="Audit Friendly" width={154} height={23} className="nav__logo-img" />
          </div>
          <p>
            The trusted resource hub for accounting and finance professionals. Software, firms,
            recruiters, and roles.
          </p>
        </div>
        <div className="footer__col">
          <h5>Jobs</h5>
          <ul>
            <li><Link to="/">Browse all roles</Link></li>
            <li><Link to="/remote-accounting-jobs">Remote jobs</Link></li>
            <li><Link to="/?internship=true">Internships</Link></li>
            {/* Goes here rather than under "Free career tools" — it is paid. */}
            <li><Link to="/lists">Recruiter contact lists</Link></li>
            <li><Link to="/post-a-job">Post a job</Link></li>
          </ul>
        </div>
        <div className="footer__col">
          <h5>Free career tools</h5>
          <ul>
            <li><Link to="/tools/salary-research">Salary Research</Link></li>
            <li><Link to="/tools/smart-match">Smart Match</Link></li>
            <li><Link to="/tools/resume-tailor">Resume Tailor</Link></li>
            <li><Link to="/tools/resume-parser">Resume Parser</Link></li>
            <li><Link to="/tools/linkedin-analyzer">LinkedIn Analyzer</Link></li>
            <li><Link to="/tools/interview-prep">Interview Prep AI</Link></li>
            <li><Link to="/tools/jobs-chat">Jobs Chat</Link></li>
            <li><Link to="/tools/market-snapshot">Market Snapshot</Link></li>
          </ul>
        </div>
        {/* Job-board pages: the paid "For your search" column (JobSearchColumn) drew
            about 10,400 views and no clicks from 2026-10-20 to 11-03, so the
            column is now the free Remote Accountant signup. */}
        {audience === "library" ? <LibraryColumn /> : <NewsletterSignupCard surface="footer" variant="column" />}
        <div className="footer__col">
          <h5>Audit Friendly</h5>
          <ul>
            <li><a href="https://auditfriendly.co/software" target="_blank" rel="noopener noreferrer">Software</a></li>
            <li><a href="https://auditfriendly.co/firms" target="_blank" rel="noopener noreferrer">Firms</a></li>
            <li><a href="https://auditfriendly.co/blog" target="_blank" rel="noopener noreferrer">Blog</a></li>
          </ul>
        </div>
        <div className="footer__col">
          <h5>Also from Audit Friendly</h5>
          <ul>
            {audience === "library" && (
              <li><a href={offerHref("still_remote", "footer")} target="_blank" rel="noopener noreferrer">Still Remote</a></li>
            )}
            <li><a href={offerHref("remote_accountant", "footer")} target="_blank" rel="noopener noreferrer">The Remote Accountant</a></li>
            <li><a href={offerHref("finance_nomad", "footer")} target="_blank" rel="noopener noreferrer">The Finance Nomad</a></li>
            <li><Link to="/lists">Recruiter lists</Link></li>
            <li><Link to="/data/api">Jobs API</Link></li>
          </ul>
        </div>
        <div className="footer__col">
          <h5>Hiring managers</h5>
          <ul>
            <li><Link to="/post-a-job">Post a job</Link></li>
            <li><Link to="/recruiter/sign-in">Sign in</Link></li>
          </ul>
        </div>
        <div className="footer__col">
          <h5>Company</h5>
          <ul>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/privacy">Privacy</Link></li>
            <li><Link to="/terms">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="footer__bottom">
        <span>© {new Date().getFullYear()} Audit Friendly</span>
        <span>hello@auditfriendly.co</span>
      </div>
    </div>
  </footer>
);

export default HomeFooter;
